import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";
import { siteStats, principles, testimonials, timeline } from "@/mocks/site";
import SubscribeForm from "./components/SubscribeForm";

export default function AboutPage() {
  useSeo({
    title: "关于我们 — 短剧爆料社的内容原则与联系方式",
    description:
      "短剧爆料社专注整理爆料短剧的演员表、导演编剧信息与拍摄花絮，坚持信息可核实、评分不掺水的内容原则，欢迎通过订阅表单与我们联系。",
    keywords: "关于短剧爆料社,演员表查询,导演编剧信息,拍摄花絮,内容原则",
    path: "/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于短剧爆料社",
        url: "https://beesfly.cn/about",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "短剧爆料社",
        url: "https://beesfly.cn/",
        email: "hello@duanju-insider.example.com",
        description:
          "整理爆料短剧的演员表、导演编剧信息、拍摄花絮与幕后制作故事，并提供剧集口碑评分。",
      },
    ],
  });

  return (
    <SiteLayout>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-content px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight text-foreground-950 md:text-4xl">
            关于短剧爆料社
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            我们是一个专注短剧主创信息的编辑团队。把散落在各处的演员表、导演编剧信息、拍摄花絮与观众评价整理到一起，
            让每一部作品都有一条清晰、可核实的资料线。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-content px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-6">
            <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.24em] text-primary-600">
              <span className="h-1 w-1 rounded-full bg-primary-500" />
              品牌介绍
            </span>
            <h2 className="mt-2.5 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
              从一份演员表开始，做到今天的资料站
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              最初它只是编辑部内部使用的一份演员表，用来核对角色与演员的对应关系。整理得越久越发现，
              观众真正缺的不是剧情简介，而是「这部剧是谁做的、怎么拍的、别人怎么看」这三件事的答案。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              于是我们把演员表查询、导演编剧信息、拍摄花絮、幕后制作故事与剧集口碑评分这五块内容合并到同一个网站，
              按作品来组织，而不是按时间流来堆叠。你查一部剧，就能看到它的全部关键信息。
            </p>

            <dl className="mt-8 grid grid-cols-2 gap-y-6 gap-x-4 border-t border-background-200 pt-6 sm:grid-cols-4">
              {siteStats.map((item) => (
                <div key={item.label}>
                  <dd className="font-heading text-2xl font-bold text-primary-600">{item.value}</dd>
                  <dt className="mt-1 text-xs text-foreground-500">{item.label}</dt>
                </div>
              ))}
            </dl>
          </div>

          <div className="lg:col-span-6">
            <div className="aspect-[4/3] w-full overflow-hidden rounded-lg border border-background-200 bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=warm%20minimal%20editorial%20workspace%20with%20film%20scripts%2C%20storyboards%20and%20a%20clapperboard%20on%20a%20wooden%20desk%2C%20soft%20afternoon%20light%2C%20crimson%20accent%20details%2C%20clean%20product%20photography%2C%20calm%20neutral%20tones&width=1200&height=900&seq=duanju-about-team&orientation=landscape"
                alt="短剧爆料社编辑部工作场景"
                title="短剧爆料社 内容团队"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-content px-4 py-12 md:px-6 md:py-16">
          <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.24em] text-primary-600">
            <span className="h-1 w-1 rounded-full bg-primary-500" />
            内容原则
          </span>
          <h2 className="mt-2.5 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
            我们给自己定了四条规矩
          </h2>

          <div className="mt-9 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {principles.map((item) => (
              <div
                key={item.title}
                className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h3 className="font-heading text-base font-bold text-foreground-950">我们走过的路</h3>
            <ol className="mt-5 flex flex-col gap-5 md:flex-row md:gap-8">
              {timeline.map((item) => (
                <li key={item.year} className="flex-1 border-l-2 border-accent-400 pl-4">
                  <span className="font-heading text-lg font-bold text-accent-700">{item.year}</span>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-content px-4 py-12 md:px-6 md:py-16">
        <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.24em] text-primary-600">
          <span className="h-1 w-1 rounded-full bg-primary-500" />
          用户口碑
        </span>
        <h2 className="mt-2.5 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
          追剧的人是怎么用这个站的
        </h2>

        <div className="mt-9 grid grid-cols-1 gap-5 md:grid-cols-3">
          {testimonials.map((item) => (
            <figure
              key={item.name}
              className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
            >
              <span className="flex h-8 w-8 items-center justify-center text-accent-500">
                <i className="ri-double-quotes-l text-2xl"></i>
              </span>
              <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                {item.text}
              </blockquote>
              <figcaption className="mt-5 border-t border-background-200 pt-4">
                <p className="font-heading text-sm font-semibold text-foreground-950">{item.name}</p>
                <p className="mt-0.5 text-xs text-foreground-500">{item.tag}</p>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="border-t border-background-200 bg-background-100">
        <div className="mx-auto grid w-full max-w-content grid-cols-1 gap-10 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.24em] text-primary-600">
              <span className="h-1 w-1 rounded-full bg-primary-500" />
              联系我们
            </span>
            <h2 className="mt-2.5 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
              补充资料、纠正信息或聊聊合作
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              如果你是剧组主创、演员经纪或资深观众，掌握我们尚未收录的资料，欢迎通过右侧表单联系我们。
              编辑部通常会在三个工作日内回复。
            </p>

            <ul className="mt-7 flex flex-col gap-4">
              <li className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-50 text-primary-600">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">邮箱</span>
                  <a
                    href="mailto:hello@duanju-insider.example.com"
                    className="text-sm text-foreground-950 underline-offset-4 hover:text-primary-600 hover:underline"
                  >
                    hello@duanju-insider.example.com
                  </a>
                </span>
              </li>
              <li className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-50 text-primary-600">
                  <i className="ri-wechat-line text-lg"></i>
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">资料补充</span>
                  <span className="text-sm text-foreground-950">请通过表单留下联系方式，我们会主动联系</span>
                </span>
              </li>
              <li className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-50 text-primary-600">
                  <i className="ri-time-line text-lg"></i>
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">回复时间</span>
                  <span className="text-sm text-foreground-950">工作日 10:00 - 19:00</span>
                </span>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-7">
            <SubscribeForm />
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}